// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useEffect, useState } from 'react'
import type { UILayout as WorkspaceLayout, UILayoutWidget } from '../types'
import type { BuilderLayout, BuilderTab, BuilderWidget, AddTabOptions } from '../types'
import {
  DEFAULT_TAB_PERMISSIONS,
  createWidgetId,
  slugify,
  ensureUniqueTabId,
  toBuilderLayout,
} from '../utils'

interface UseBuilderLayoutOptions {
  initialLayout?: WorkspaceLayout | null
}

export const useBuilderLayout = ({ initialLayout }: UseBuilderLayoutOptions) => {
  const [layout, setLayout] = useState<BuilderLayout>(() => toBuilderLayout(initialLayout))

  // Update layout when initialLayout prop changes
  useEffect(() => {
    setLayout(toBuilderLayout(initialLayout))
  }, [initialLayout])

  const tabs = layout.tabs

  const addTab = useCallback((options?: AddTabOptions) => {
    const index = layout.tabs.length + 1
    const label = options?.label?.trim() || `Tab ${index}`
    const existingIds = new Set(layout.tabs.map(tab => tab.id))
    const resolvedId = ensureUniqueTabId(existingIds, slugify(options?.id ?? label))
    const newTab: BuilderTab = {
      id: resolvedId,
      label,
      order: index,
      permissions: options?.permissions?.length ? options.permissions : [...DEFAULT_TAB_PERMISSIONS],
      widgets: [],
    }

    setLayout(prev => ({
      ...prev,
      tabs: [...prev.tabs, newTab],
      defaultTab: prev.defaultTab ?? newTab.id,
    }))

    return newTab
  }, [layout.tabs])

  const updateTab = useCallback((tabId: string, updater: (tab: BuilderTab) => BuilderTab) => {
    setLayout(prev => ({
      ...prev,
      tabs: prev.tabs.map(tab => (tab.id === tabId ? updater(tab) : tab)),
    }))
  }, [])

  const removeTab = useCallback((tabId: string) => {
    setLayout(prev => {
      const nextTabs = prev.tabs.filter(tab => tab.id !== tabId)
      const nextDefault = prev.defaultTab === tabId ? nextTabs[0]?.id : prev.defaultTab
      return {
        ...prev,
        tabs: nextTabs,
        defaultTab: nextDefault,
      }
    })
  }, [])

  const addWidgetToTab = useCallback((tabId: string, widget: UILayoutWidget) => {
    updateTab(tabId, tab => {
      const order = tab.widgets.length
      const builderWidget: BuilderWidget = {
        ...widget,
        id: createWidgetId(tabId, widget.component),
        order,
      }
      return {
        ...tab,
        widgets: [...tab.widgets, builderWidget],
      }
    })
  }, [updateTab])

  const removeWidgetFromTab = useCallback((tabId: string, widgetId: string) => {
    updateTab(tabId, tab => ({
      ...tab,
      widgets: tab.widgets.filter(widget => widget.id !== widgetId),
    }))
  }, [updateTab])

  const setDefaultTab = useCallback((tabId: string) => {
    setLayout(prev => ({
      ...prev,
      defaultTab: tabId,
    }))
  }, [])

  return {
    layout,
    tabs,
    addTab,
    removeTab,
    updateTab,
    addWidgetToTab,
    removeWidgetFromTab,
    setDefaultTab,
  }
}
